<script setup>
import { ref, computed, onMounted, onUnmounted, reactive } from 'vue'
import { storeToRefs } from 'pinia'
import { useUsersStore, useRestaurantsStore, useReservationsStore } from '@/stores'
import RestaurantGridCard from "@core/components/home/content/RestaurantGridCard.vue"
import { orderCities } from '@/utils/cityOrder'
import CmsText from '@/@core/components/CmsText.vue'

// Show the Request Free Restaurant Account button between the tabbed card
// and the Preferred list (used by the home page's Restaurants expand panel;
// the standalone /restaurants page has its own button in the masthead)
const props = defineProps({
  showRequestAccountButton: {
    type: Boolean,
    default: false
  },

  // The home page's Restaurants expand panel hides the Preferred list
  // (it stays on the standalone /restaurants page)
  showPreferredList: {
    type: Boolean,
    default: true
  }
})

const usersStore = useUsersStore()
const restaurantsStore = useRestaurantsStore()
const reservationsStore = useReservationsStore()

const myUser = computed(() => usersStore.myUser)
const { preferredRestaurants, citySelectorData } = storeToRefs(restaurantsStore)
const { editingReservation } = storeToRefs(reservationsStore)
let loginDialogMode = null
let loginDialogVerificationType = null;
({ loginDialogMode, loginDialogVerificationType } = storeToRefs(usersStore))

const tab = ref('how-rm-works')

// City filter state — defaults to the app's city context
const DEFAULT_MARKET = 'Chicago, IL'
const selectedCity = ref(DEFAULT_MARKET)

// City filter bottom sheet state
const isCityFilterOpen = ref(false)
const citySearchQuery = ref('')
const pendingCitySelection = ref('')

// City selector options from backend
const selectableCities = computed(() => {
  // Most-requested markets first, then All Cities, then the rest A–Z
  return orderCities(citySelectorData.value?.cities || [])
})

// Filtered cities for bottom sheet search
const filteredCityOptions = computed(() => {
  const cities = selectableCities.value
  if (!citySearchQuery.value) return cities
  const query = citySearchQuery.value.toLowerCase()

  return cities.filter(c => c.text.toLowerCase().includes(query))
})

// Open city filter bottom sheet
const openCityFilter = () => {
  pendingCitySelection.value = selectedCity.value
  citySearchQuery.value = ''
  isCityFilterOpen.value = true
  handleCitySelectorFocus()
}

// Apply city filter selection from bottom sheet
const applyCityFilter = () => {
  selectedCity.value = pendingCitySelection.value
  isCityFilterOpen.value = false
  handleCityChange()
}

// Pagination and loading state
const isLoading = ref(false)
const isLoadingMore = ref(false)
const hasMoreData = ref(true)
const isPrefetching = ref(false)
const hasPrefetchedNext = ref(false)

const pagination = reactive({
  // 60 per page so "All Cities" shows a multi-city mix on first paint
  // (the first alphabetical page skews Chicago); scroll pages the rest
  limit: 60,
  skip: 0,
  prefetchedData: null
})

// Get filtered/sorted preferred restaurants (always alphabetical)
const displayedRestaurants = computed(() => {
  if (!preferredRestaurants.value) return []

  let filtered = preferredRestaurants.value.filter(r => r.status === 'Preferred')

  // Apply city filter
  if (selectedCity.value) {
    filtered = filtered.filter(r => r.market === selectedCity.value)
  }

  // Always sort alphabetically
  filtered = [...filtered].sort((a, b) => {
    const nameA = (a.name || '').toLowerCase()
    const nameB = (b.name || '').toLowerCase()

    return nameA.localeCompare(nameB)
  })

  return filtered
})

// Load initial preferred restaurants data
const loadPreferredRestaurants = async (city = null, reset = true) => {
  isLoading.value = true
  try {
    if (reset) {
      pagination.skip = 0
      pagination.prefetchedData = null
      hasMoreData.value = true
      hasPrefetchedNext.value = false
    }

    await restaurantsStore.getPreferredRestaurants(pagination.limit, city, pagination.skip, false)
  } finally {
    isLoading.value = false
  }
}

// Prefetch next page of restaurants in background
const prefetchNextPage = async () => {
  // Don't prefetch if dialog is open
  if (editingReservation?.value) return
  if (isPrefetching.value || !hasMoreData.value || hasPrefetchedNext.value) return

  isPrefetching.value = true
  try {
    const nextSkip = pagination.skip + pagination.limit

    const prefetchedData = await restaurantsStore.getPreferredRestaurants(
      pagination.limit,
      selectedCity.value || null,
      nextSkip,
      false, // Don't append
      true // fetchOnly - don't update store, just return data
    )

    pagination.prefetchedData = prefetchedData
    hasPrefetchedNext.value = true

    if (!prefetchedData || prefetchedData.length < pagination.limit) {
      hasMoreData.value = false
    }
  } catch (error) {
    console.error('Error prefetching:', error)
  } finally {
    isPrefetching.value = false
  }
}

// Load more restaurants for infinite scrolling
const loadMoreRestaurants = async () => {
  // Don't load more if dialog is open
  if (editingReservation?.value) return
  if (isLoadingMore.value || !hasMoreData.value) return

  isLoadingMore.value = true
  try {
    if (pagination.prefetchedData) {
      // Use cached prefetched data - instant!
      const cachedData = pagination.prefetchedData
      restaurantsStore.preferredRestaurants = [...restaurantsStore.preferredRestaurants, ...cachedData]

      pagination.skip += pagination.limit
      pagination.prefetchedData = null
      hasPrefetchedNext.value = false

      // Immediately start prefetching next batch
      setTimeout(() => prefetchNextPage(), 100)
    } else {
      // Fallback: fetch if no prefetched data available
      pagination.skip += pagination.limit

      const newData = await restaurantsStore.getPreferredRestaurants(
        pagination.limit,
        selectedCity.value || null,
        pagination.skip,
        true // append to existing
      )

      if (!newData || newData.length < pagination.limit) {
        hasMoreData.value = false
      }

      setTimeout(() => prefetchNextPage(), 100)
    }
  } finally {
    isLoadingMore.value = false
  }
}

// Handle city selection change
const handleCityChange = async () => {
  hasMoreData.value = true
  hasPrefetchedNext.value = false
  await loadPreferredRestaurants(selectedCity.value || null, true)
}

// Load city selector data (lazy load)
const handleCitySelectorFocus = async () => {
  if (!citySelectorData.value || !citySelectorData.value.cities) {
    try {
      await restaurantsStore.getCitySelectorData()
    } catch (error) {
      console.error('Error loading city selector data:', error)
    }
  }
}

// Infinite scrolling in one scrolling document: thresholds are measured
// against this component's own bottom edge, so pagination works the same on
// the standalone /restaurants page and embedded mid-page (home expand panel,
// where content continues below the list).
const rootEl = ref(null)

// Handle scroll event for infinite scrolling
const handleScroll = () => {
  const el = rootEl.value
  if (!el) return

  // Distance from the viewport bottom to the end of this component's list
  const distanceToListEnd = el.getBoundingClientRect().bottom - window.innerHeight

  // Start prefetching within two screens of the list's end
  if (distanceToListEnd < window.innerHeight * 2 && !hasPrefetchedNext.value && !isPrefetching.value && hasMoreData.value) {
    prefetchNextPage()
  }

  // Load more when within 800px of the list's end
  if (distanceToListEnd < 800) {
    loadMoreRestaurants()
  }
}

// Load data on mount (skipped entirely when the Preferred list is hidden)
onMounted(async () => {
  if (!props.showPreferredList) return

  // Check if data is already loaded
  const hasData = preferredRestaurants.value && preferredRestaurants.value.length > 0

  if (!hasData) {
    await loadPreferredRestaurants(selectedCity.value || null)
  }

  // Load city selector data if not already loaded
  if (!citySelectorData.value || !citySelectorData.value.cities) {
    restaurantsStore.getCitySelectorData()
  }

  window.addEventListener('scroll', handleScroll)
})

onUnmounted(() => {
  if (!props.showPreferredList) return
  window.removeEventListener('scroll', handleScroll)
})
</script>

<template>
  <div ref="rootEl">
    <div
      style="display: flex;"
      class="justify-center mt-5 mb-8"
    >
      <div class="content-wrapper tab-content-width">
        <VCard
          class="px-5 py-6 pb-4 tabs-card"
          flat
        >
          <!--
            Plain buttons instead of VTabs (same as the Members panel):
            Vuetify's slide-group mis-measures when mounted mid-expand 
          -->
          <div class="simple-tabs">
            <button
              class="simple-tab"
              :class="{ 'simple-tab--active': tab === 'how-rm-works' }"
              type="button"
              @click="tab = 'how-rm-works'"
            >
              Overview
            </button>
            <button
              class="simple-tab"
              :class="{ 'simple-tab--active': tab === 'preferred-benefits' }"
              type="button"
              @click="tab = 'preferred-benefits'"
            >
              Preferred Benefits
            </button>
            <button
              class="simple-tab"
              :class="{ 'simple-tab--active': tab === 'faqs' }"
              type="button"
              @click="tab = 'faqs'"
            >
              FAQs
            </button>
          </div>
          
          <VDivider />
          
          <VWindow v-model="tab">
            <VWindowItem
              class="pt-4 text-xs"
              value="how-rm-works"
            >
              <div class="content-wrapper tab-content-width">
                <CmsText
                  k="restaurants.tabs.howRmWorks"
                  tag="div"
                  class="section-subtitle text-left mt-2 tab-content overview-content"
                  style="line-height: 1.4em;"
                />
              </div>
            </VWindowItem>

            <VWindowItem
              class="pt-4 text-xs"
              value="preferred-benefits"
            >
              <div class="content-wrapper tab-content-width">
                <CmsText
                  k="restaurants.tabs.preferredBenefits"
                  tag="div"
                  class="section-subtitle text-left mt-2 tab-content"
                  style="line-height: 1.4em;"
                />
              </div>
            </VWindowItem>

            <VWindowItem
              class="pt-4 text-xs"
              value="faqs"
            >
              <div class="content-wrapper tab-content-width">
                <CmsText
                  k="restaurants.tabs.faqs"
                  tag="div"
                  class="section-subtitle text-left mt-2 tab-content"
                  style="line-height: 1.4em;"
                />
              </div>
            </VWindowItem>
          </VWindow>
        </VCard>
      </div>
    </div>

    <!-- Request account CTA — below the tabbed card, visible on any tab -->
    <div
      v-if="props.showRequestAccountButton"
      class="text-center mb-8"
    >
      <VBtn
        variant="flat"
        size="small"
        color="primary"
        @click="loginDialogVerificationType = 'restaurant'; loginDialogMode = 'activate';"
      >
        <span>Request Free Restaurant Account</span>
      </VBtn>
    </div>

    <!-- Preferred Restaurants List -->
    <div
      v-if="props.showPreferredList"
      class="preferred-restaurants-section"
    >
      <h3 class="section-header text-center mb-4">
        Preferred Restaurants
      </h3>

      <!-- City Filter Trigger -->
      <div class="city-filter-wrapper mb-4">
        <div
          class="city-filter-trigger"
          @click="openCityFilter"
        >
          <VIcon
            icon="mdi-map-marker"
            size="18"
            color="primary-dark"
          />
          <span class="city-filter-trigger-text">{{ selectableCities.find(c => c.value === selectedCity)?.text || selectedCity || 'All Cities' }}</span>
          <VIcon
            icon="mdi-chevron-down"
            size="18"
          />
        </div>
      </div>

      <!-- Restaurant Grid -->
      <div
        v-if="displayedRestaurants.length > 0"
        class="restaurant-list-wrapper"
      >
        <div class="restaurant-list">
          <RestaurantGridCard
            v-for="(item, index) in displayedRestaurants"
            :key="item._id || item.raw?._id"
            :item="item"
            :index="index"
            @open-choose-member-dialog="(market, restaurant) => usersStore.startNewReservation({ restaurant, market })"
          />
        </div>
      </div>

      <!-- Loading State -->
      <div
        v-else-if="isLoading"
        class="loading-state"
      >
        <VProgressCircular
          indeterminate
          color="primary"
          size="64"
        />
      </div>

      <!-- Empty State -->
      <div
        v-else
        class="empty-state"
      >
        <p class="text-center text-grey">
          No preferred restaurants found{{ selectedCity ? ' for this city' : '' }}.
        </p>
      </div>

      <!-- Prefetching Indicator -->
      <div
        v-if="isPrefetching && displayedRestaurants.length > 0 && !isLoading"
        class="prefetching-indicator"
      >
        <VProgressCircular
          indeterminate
          color="primary"
          size="24"
        />
        <p class="prefetching-text">
          Loading more...
        </p>
      </div>
    </div>

    <!-- City Filter Bottom Sheet -->
    <VBottomSheet
      v-if="props.showPreferredList"
      v-model="isCityFilterOpen"
    >
      <VCard class="city-filter-sheet">
        <div class="sheet-handle" />
        <div class="sheet-header">
          <span class="sheet-title">Filter by City</span>
          <VBtn
            icon
            variant="text"
            size="small"
            @click="isCityFilterOpen = false"
          >
            <VIcon size="20">
              mdi-close
            </VIcon>
          </VBtn>
        </div>
        <VDivider />

        <!-- Search -->
        <div class="city-search-wrapper">
          <VTextField
            v-model="citySearchQuery"
            placeholder="Search cities..."
            variant="outlined"
            density="compact"
            hide-details
            prepend-inner-icon="mdi-magnify"
            class="city-search-input"
          />
        </div>

        <!-- City list -->
        <div class="city-list">
          <div
            v-for="city in filteredCityOptions"
            :key="city.value"
            class="city-list-item"
            :class="{ 'city-list-item--boundary': !city.value }"
            @click="pendingCitySelection = city.value"
          >
            <span class="city-list-item-text">{{ city.text }}</span>
            <span
              class="city-radio"
              :class="{ 'city-radio--selected': pendingCitySelection === city.value }"
            />
          </div>
        </div>

        <!-- Done button -->
        <div class="city-done-wrapper">
          <VBtn
            block
            color="primary-dark"
            rounded="lg"
            size="large"
            @click="applyCityFilter"
          >
            Done
          </VBtn>
        </div>
      </VCard>
    </VBottomSheet>
  </div>
</template>

<style lang="scss" scoped>
  .tab-content-width {
    max-width: 460px;
    font-size: 0.8em;
  }

  .simple-tabs {
    display: flex;
    width: 100%;
  }

  .simple-tab {
    flex: 1;
    min-width: 0;
    padding: 10px 8px 8px;
    text-align: center;
    font-family: var(--ds-font-body);
    font-size: 0.75rem;
    font-weight: 500;
    letter-spacing: 0.02em;
    line-height: var(--ds-leading-tight);
    color: var(--ds-text-secondary);
    background: none;
    border: none;
    border-bottom: 2px solid transparent;
    cursor: pointer;
    transition: color 0.15s ease, border-color 0.15s ease;
  }

  @media (max-width: 600px) {
    .simple-tab {
      padding: 10px 4px 8px;
      font-size: 0.7rem;
    }
  }

  .simple-tab--active {
    color: rgb(var(--v-theme-primary));
    border-bottom-color: rgb(var(--v-theme-primary));
    font-weight: 600;
  }

  .tab-content {
    font-family: inherit !important;
    color: var(--ds-text-primary) !important;
  }

  // Overview tab: bold lead line, then individually stacked statements
  // (distinct points, deliberately not a bulleted list)
  .tab-content.overview-content :deep(p) {
    margin: 0 0 1rem !important;
    line-height: 1.5 !important;
  }

  .tab-content.overview-content :deep(p:first-of-type) {
    font-weight: 700;
    margin-bottom: 1.1rem !important;
  }

  .tab-content :deep(p) {
    margin: 0 !important;
    font-family: inherit !important;
    font-size: 0.75rem !important;
    line-height: 16px !important;
    color: var(--ds-text-primary) !important;
  }

  .tab-content :deep(b) {
    font-family: inherit !important;
    color: var(--ds-text-primary) !important;
  }

  .tab-content :deep(a) {
    color: rgb(var(--v-theme-primary));
  }

  .tab-content :deep(p.section-title) {
    margin-top: 1rem !important;
  }

  .tab-content :deep(p.section-title:first-child) {
    margin-top: 0 !important;
  }

  .preferred-restaurants-section {
    width: 100%;
    max-width: 500px;
    margin: 0 auto;
    padding: 0 var(--ds-space-4);
  }

  .section-header {
    font-family: var(--ds-font-body);
    font-size: var(--ds-text-lg);
    font-weight: 600;
    color: var(--ds-brand-primary-dark);
    margin-bottom: var(--ds-space-4);
  }

  // City filter trigger button
  .city-filter-wrapper {
    display: flex;
    justify-content: center;
  }

  .city-filter-trigger {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    border-radius: var(--ds-radius-md);
    background: var(--ds-surface-subtle);
    padding: var(--ds-space-2) var(--ds-space-4);
    cursor: pointer;
    transition: background var(--ds-duration-normal) ease;
    width: fit-content;

    &:hover {
      background: #ebebeb;
    }
  }

  .city-filter-trigger-text {
    font-family: var(--ds-font-body);
    font-size: var(--ds-text-md);
    font-weight: 500;
    color: var(--ds-brand-primary-dark);
  }

  // City filter bottom sheet
  .city-filter-sheet {
    border-radius: var(--ds-radius-lg) var(--ds-radius-lg) 0 0 !important;
    min-block-size: 70vh;
    max-block-size: 70vh;
    display: flex;
    flex-direction: column;
  }

  .sheet-handle {
    width: 36px;
    height: 4px;
    border-radius: var(--ds-radius-xs);
    background: rgba(var(--v-theme-on-surface), 0.2);
    margin: 10px auto 4px;
  }

  .sheet-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 16px 8px 20px;
  }

  .sheet-title {
    font-size: var(--ds-text-md);
    font-weight: 600;
    color: rgba(var(--v-theme-on-surface), 0.87);
  }

  .city-search-wrapper {
    padding: 12px 16px 8px;
  }

  .city-search-input {
    :deep(.v-field) {
      border-radius: var(--ds-radius-sm);
      font-size: 16px; // prevents iOS zoom
    }
  }

  .city-list {
    overflow-y: auto;
    flex: 1;
    padding: 0 4px;
  }

  // Crisp explicit border instead of Vuetify's downward elevation shadow,
  // whose top edge renders nearly invisible (read as a "broken" border)
  .tabs-card {
    border: 1px solid var(--ds-border-light);
    border-radius: 12px;
    box-shadow: none !important;
  }

  .city-list-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    cursor: pointer;
    border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.06);

    // Boundary under the All Cities row: pinned most-requested markets above,
    // alphabetical list below
    &.city-list-item--boundary {
      border-bottom: 2px solid var(--ds-border-strong);
    }

    &:last-child {
      border-bottom: none;
    }

    &:active {
      background: rgba(var(--v-theme-on-surface), 0.04);
    }
  }

  .city-list-item-text {
    font-family: var(--ds-font-body);
    font-size: var(--ds-text-md);
    font-weight: 400;
    color: rgba(var(--v-theme-on-surface), 0.87);
  }

  .city-radio {
    width: 22px;
    height: 22px;
    border-radius: var(--ds-radius-full);
    border: 2px solid rgba(var(--v-theme-on-surface), 0.3);
    flex-shrink: 0;
    transition: all var(--ds-duration-fast) ease;

    &--selected {
      border-color: var(--ds-brand-primary-dark);
      background: var(--ds-brand-primary-dark);
      box-shadow: inset 0 0 0 3px #fff;
    }
  }

  .city-done-wrapper {
    padding: 12px 16px;
    border-top: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  }

  .restaurant-list-wrapper {
    width: 100%;
  }

  .restaurant-list {
    display: flex;
    flex-direction: column;
    gap: var(--ds-space-3);
    width: 100%;
  }

  .loading-state {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 40px 20px;
    min-height: 200px;
  }

  .empty-state {
    padding: 40px 20px;
    text-align: center;
    color: var(--ds-text-muted);
  }

  .prefetching-indicator {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--ds-space-2);
    padding: var(--ds-space-6) 0;
  }

  .prefetching-text {
    font-family: var(--ds-font-body);
    font-size: var(--ds-text-base);
    color: var(--ds-text-muted);
    margin: 0;
  }
</style>

